import {
    SET_DEALER_CLIENT,
    SET_NOTES,
    SET_SELECTED_ACTIVITIES,
    SET_SELECTED_EXTRAS,
    SET_SELECTED_ROOMS,
    SET_TRIP_ID,
    SET_REFUNDABLE,
    SET_GUEST_INFO
} from "../constants/transactionConstants";
import {v4 as uuidv4} from "uuid";
import {setTotalActivitiesFH, setTotals} from "./totalActions";
import {canAddSingleSpace, canChangeGender, getNewSelectedRooms} from "../../utils/utils";


export const addSelectedSpace = (roomId, singleOccupancy = false) => (dispatch, getState) => {
    const {transactions, rooms} = getState();
    const selectedRooms = transactions.selectedRooms;

    const roomType = rooms.find(r => r.index === roomId);
    const roomTypeSelection = selectedRooms.filter(s => s.index === roomId);

    if (singleOccupancy && !canAddSingleSpace(roomTypeSelection, roomType, singleOccupancy)) return;

    const id = uuidv4();

    const newItem = {
        id, index: roomId, singleOccupancy: false, sex: '',
    };

    // Only apply single occupancy in case there are no others of the room type
    if (singleOccupancy && !roomTypeSelection.some(r => r.singleOccupancy))
        newItem['singleOccupancy'] = true;

    const newSelectedRooms = [...selectedRooms, newItem];

    dispatch({
        type: SET_SELECTED_ROOMS,
        payload: newSelectedRooms
    });

    // Store the latest transactions in sessionStorage
    sessionStorage.setItem('transactions', JSON.stringify({...transactions, selectedRooms: newSelectedRooms}));

    dispatch(setTotals());
};

export const removeSelectedSpace = (roomId) => (dispatch, getState) => {
    const {transactions} = getState();
    const selectedRooms = transactions.selectedRooms;

    const selectionToRemove = [...selectedRooms].reverse().find(r => r.index === roomId);
    const newSelectedRooms = [...selectedRooms].filter(r => r.id !== selectionToRemove.id);

    dispatch({
        type: SET_SELECTED_ROOMS,
        payload: newSelectedRooms
    });

    // Store the latest transactions in sessionStorage
    sessionStorage.setItem('transactions', JSON.stringify({...transactions, selectedRooms: newSelectedRooms}));

    dispatch(setTotals());
}

export const setSpaceGender = (selectionId, gender) => (dispatch, getState) => {
    const {transactions, rooms} = getState();
    const selectedRooms = transactions.selectedRooms;

    const currentRoom = selectedRooms.find(s => s.id === selectionId);
    const roomType = rooms.find(r => r.index === currentRoom.index);

    if (!canChangeGender(selectedRooms, selectionId, roomType, gender)) return;

    const newSelectedRooms = getNewSelectedRooms(selectedRooms, roomType, selectionId, gender);

    dispatch({
        type: SET_SELECTED_ROOMS,
        payload: newSelectedRooms
    });

    // Store the latest transactions in sessionStorage
    sessionStorage.setItem('transactions', JSON.stringify({...transactions, selectedRooms: newSelectedRooms}));
};

export const setComplimentaryActivities = (activityId, availabilityId, active) => (dispatch, getState) => {
    const {transactions} = getState();
    const complimentary = transactions.activities || {};

    let newComplimentary = {...complimentary};

    const formattedId = `${availabilityId}`;

    if (active) {
        newComplimentary[activityId] = formattedId;
    } else {
        delete newComplimentary[activityId];
    }

    dispatch({
        type: SET_SELECTED_ACTIVITIES,
        payload: newComplimentary
    });

    sessionStorage.setItem('transactions', JSON.stringify({...transactions, activities: newComplimentary}));

    dispatch(setTotals());
};


export const setExtraActivities = (activityId, availabilityId, active) => (dispatch, getState) => {
    const {transactions} = getState();
    const extras = transactions.extra;

    let newExtraTransactions = {...extras};

    if (!active)
        delete newExtraTransactions[activityId];
    else
        newExtraTransactions[activityId] = availabilityId;

    dispatch({
        type: SET_SELECTED_EXTRAS,
        payload: newExtraTransactions
    });

    // Store the latest transactions in sessionStorage
    sessionStorage.setItem('transactions', JSON.stringify({...transactions, extra: newExtraTransactions}));

    dispatch(setTotals());
};

export const setDealerActivities = (company, activityId, availabilityId, availabilityData, active) => (dispatch, getState) => {
    const {transactions} = getState();
    const extras = transactions.extra;

    const newExtraTransactions = {...extras};

    if (!active) {
        // Eliminar esa entrada específica
        if (
            newExtraTransactions[company] &&
            newExtraTransactions[company][activityId] &&
            newExtraTransactions[company][activityId][availabilityId]
        ) {
            delete newExtraTransactions[company][activityId][availabilityId];

            // Limpieza si ya no quedan entradas
            if (Object.keys(newExtraTransactions[company][activityId]).length === 0) {
                delete newExtraTransactions[company][activityId];
            }
            if (Object.keys(newExtraTransactions[company]).length === 0) {
                delete newExtraTransactions[company];
            }
        }
    } else {
        if (!newExtraTransactions[company]) {
            newExtraTransactions[company] = {};
        }
        if (!newExtraTransactions[company][activityId]) {
            newExtraTransactions[company][activityId] = {};
        }
        newExtraTransactions[company][activityId][availabilityId] = availabilityData;
    }

    dispatch({
        type: SET_SELECTED_EXTRAS,
        payload: newExtraTransactions
    });

    sessionStorage.setItem(
        'transactions',
        JSON.stringify({...transactions, extra: newExtraTransactions})
    );

    dispatch(setTotalActivitiesFH());
};

export const replaceLatestTempIdWithRealId = (company, activityId, availability) => (dispatch, getState) => {
    const {transactions} = getState();
    const extra = transactions.extra;
    const realId = availability.id;

    if (!extra?.[company]?.[activityId]) return extra;

    const entries = Object.entries(extra[company][activityId]);
    const tempEntries = entries
        .filter(([key]) => key.startsWith('temp_'))
        .sort((a, b) => {
            const aTime = parseInt(a[0].replace('temp_', ''));
            const bTime = parseInt(b[0].replace('temp_', ''));
            return bTime - aTime;
        });

    if (tempEntries.length === 0) return setDealerActivities(company, activityId, availability.id, availability, true);

    const latestTempId = tempEntries[0][0];

    // Reemplazar el ID temporal por el real
    const updated = {...extra};
    const companyExtras = {...updated[company]};
    const activityExtras = {...companyExtras[activityId]};

    delete activityExtras[latestTempId];
    activityExtras[realId] = availability;

    companyExtras[activityId] = activityExtras;
    updated[company] = companyExtras;

    dispatch({
        type: SET_SELECTED_EXTRAS,
        payload: updated
    });

    sessionStorage.setItem(
        'transactions',
        JSON.stringify({...transactions, extra: updated})
    );
    dispatch(setTotalActivitiesFH());
};


export const setTripId = (tripId) => (dispatch) => {
    dispatch({
        type: SET_TRIP_ID,
        payload: tripId
    });
};

export const setTransactionNotes = (notes) => (dispatch, getState) => {
    const {transactions} = getState();
    sessionStorage.setItem('transactions', JSON.stringify({...transactions, notes: notes}));
    dispatch({type: SET_NOTES, payload: notes});
};

export const setTransactionInfoWidget = (guestInfo) => (dispatch, getState) => {
    const {transactions} = getState();
    sessionStorage.setItem('transactions', JSON.stringify({...transactions, guestInfo: guestInfo}));
    dispatch({type: SET_GUEST_INFO, payload: guestInfo});
};

export const setTransactionClient = (clientName, clientBookingRef) => (dispatch, getState) => {
    const {transactions} = getState();
    sessionStorage.setItem('transactions', JSON.stringify({...transactions, client: {clientName, clientBookingRef}}));
    dispatch({type: SET_DEALER_CLIENT, payload: {clientName, clientBookingRef}});
};

export const setTransactionRefundable = (refundable) => (dispatch, getState) => {
    const {transactions} = getState();
    sessionStorage.setItem('transactions', JSON.stringify({...transactions, refundable: refundable}));
    dispatch({type: SET_REFUNDABLE, payload: refundable});
}